/**
 * Word Search Reward Animation
 *
 * Plays a celebratory animation when a word is found:
 * 1. Letters bounce one-by-one to double size (500-800ms per letter)
 * 2. Letter background flashes golden at peak bounce
 * 3. Star particles rise from each letter toward the achievements nav icon
 * 4. Stars rise straight up halfway, then curve toward achievements icon
 * 5. Stars fade opacity 1→0 and shrink 80px→10px as they reach the icon
 * 6. Sound effect plays (wow.mp3)
 * 7. Letters return to normal color after sequence
 *
 * Toggle: set SHOW_WORD_SEARCH_REWARD_ANIMATION = false to disable entirely.
 */

// ═══════════════════════════════════════════════════════════════════
// CONFIGURATION — tweak these to adjust the animation
// ═══════════════════════════════════════════════════════════════════

/** Master toggle — set to false to disable all reward animations */
export const SHOW_WORD_SEARCH_REWARD_ANIMATION = true;

/** Duration of each letter bounce (ms) */
export const LETTER_BOUNCE_DURATION_MS = 600;

/** Delay between each letter starting its bounce (ms) */
export const LETTER_STAGGER_MS = 120;

/** Scale factor at peak bounce (2 = double size) */
export const LETTER_BOUNCE_SCALE = 2;

/** Golden flash color at peak bounce */
export const GOLDEN_FLASH_COLOR = '#FFD700';

/** Dark brown text color for golden state */
export const GOLDEN_TEXT_COLOR = '#3F2A14';

/** Border style during bounce */
export const BOUNCE_BORDER = '1px solid #000';

/** How long the golden state holds after animation completes (ms) */
export const GOLDEN_HOLD_DURATION_MS = 2500;

/** Number of star particles per letter */
export const STARS_PER_LETTER = 3;

/** Star particle starting size (px) — random between min and max */
export const STAR_MIN_SIZE = 30;
export const STAR_MAX_SIZE = 50;

/** Star particle max size at midpoint (px) */
export const STAR_MID_SIZE = 80;

/** Star particle end size at destination (px) */
export const STAR_END_SIZE = 10;

/** Minimum spacing between stars (px) */
export const STAR_MIN_SPACING = 5;

/** Total star travel duration (ms) */
export const STAR_TRAVEL_DURATION_MS = 1400;

/** Sound effect path (default / hard level) */
export const REWARD_SOUND_PATH = '/audio/word-search-difficult-word-win.mp3';

/** Sound effect path for easy level */
export const REWARD_SOUND_PATH_EASY = '/audio/word-search-easy-word-win.mp3';

/** Sound effect path for medium level */
export const REWARD_SOUND_PATH_MEDIUM = '/audio/word-search-medium-word-win.mp3';

/** Sound effect volume (0-1) */
export const REWARD_SOUND_VOLUME = 0.8;

/** Star image URLs (same colorful stars from story reader) */
const STAR_URLS = [
  '/svg/components/YellowStar1.svg',
  '/svg/components/RedStar1.svg',
  '/svg/components/PinkStar1.svg',
  '/svg/components/GreenStar1.svg',
  '/svg/components/BlueStar1.svg',
];

// ═══════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════

/**
 * Play the word-found reward animation on the given cells.
 *
 * @param {HTMLElement[]} cellElements - The DOM elements of the found word's cells (in order)
 * @param {string} [difficulty] - Game difficulty ('easy', 'medium', 'hard') for sound selection
 * @returns {Promise<void>} Resolves when the full animation sequence is complete
 */
export async function playWordFoundReward(cellElements, difficulty) {
  if (!SHOW_WORD_SEARCH_REWARD_ANIMATION) return;
  if (!cellElements || cellElements.length === 0) return;

  // Play sound (easy level uses banjo)
  playRewardSound(difficulty);
  playRewardSound();

  // Run the letter bounce + star sequence
  await animateLetterSequence(cellElements);
}

// ═══════════════════════════════════════════════════════════════════
// INTERNAL — Letter Bounce Animation
// ═══════════════════════════════════════════════════════════════════

/**
 * Animate letters one by one: bounce + golden flash + emit stars.
 * After all letters complete, hold golden for GOLDEN_HOLD_DURATION_MS then restore.
 *
 * @param {HTMLElement[]} cells - Ordered array of cell DOM elements
 */
async function animateLetterSequence(cells) {
  // Store original styles to restore later
  const originalStyles = cells.map((cell) => ({
    background: cell.style.background || '',
    color: cell.style.color || '',
    border: cell.style.border || '',
  }));

  // Get achievements icon position (computed once per sequence)
  const achievementsTarget = getAchievementsPosition();

  // Create star particle container
  const starContainer = createStarContainer();

  // Stagger each letter
  const promises = cells.map((cell, index) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        bounceLetter(cell, () => {
          // At peak: emit star particles
          emitStarsFromCell(cell, starContainer, achievementsTarget);
        });

        // Resolve after bounce duration completes
        setTimeout(resolve, LETTER_BOUNCE_DURATION_MS);
      }, index * LETTER_STAGGER_MS);
    });
  });

  await Promise.all(promises);

  // After bounce sequence: all cells stay golden, no border
  cells.forEach((cell) => {
    cell.style.background = GOLDEN_FLASH_COLOR;
    cell.style.color = GOLDEN_TEXT_COLOR;
    cell.style.border = 'none';
  });

  // Wait for star particles to finish traveling
  await delay(STAR_TRAVEL_DURATION_MS);

  // Hold golden state
  await delay(GOLDEN_HOLD_DURATION_MS);

  // Restore original cell styles
  cells.forEach((cell, i) => {
    cell.style.background = originalStyles[i].background;
    cell.style.color = originalStyles[i].color;
    cell.style.border = originalStyles[i].border;
  });

  // Cleanup star container
  starContainer.remove();
}

/**
 * Bounce a single letter cell: scale up, flash golden, scale back down.
 * Border fades from opacity 1 to 0 during bounce. Gold stays.
 *
 * @param {HTMLElement} cell - The grid cell element
 * @param {Function} onPeak - Callback fired at peak of bounce (for star emission)
 */
function bounceLetter(cell, onPeak) {
  const halfDuration = LETTER_BOUNCE_DURATION_MS / 2;

  // Phase 1: scale up + golden background + dark brown text + border visible
  cell.style.transition = `transform ${halfDuration}ms cubic-bezier(0.34, 1.56, 0.64, 1), background ${halfDuration}ms ease, border-color ${LETTER_BOUNCE_DURATION_MS}ms ease`;
  cell.style.transform = `scale(${LETTER_BOUNCE_SCALE})`;
  cell.style.background = GOLDEN_FLASH_COLOR;
  cell.style.color = GOLDEN_TEXT_COLOR;
  cell.style.border = BOUNCE_BORDER;
  cell.style.zIndex = '100';

  // Start fading border to transparent over full bounce duration
  requestAnimationFrame(() => {
    cell.style.borderColor = 'transparent';
  });

  // At peak: fire callback
  setTimeout(() => {
    if (onPeak) onPeak();

    // Phase 2: scale back down, keep golden, border continues fading
    cell.style.transition = `transform ${halfDuration}ms ease-in`;
    cell.style.transform = 'scale(1)';
    cell.style.zIndex = '';
  }, halfDuration);
}

// ═══════════════════════════════════════════════════════════════════
// INTERNAL — Star Particles
// ═══════════════════════════════════════════════════════════════════

/**
 * Create a fixed-position container for star particles.
 * @returns {HTMLElement}
 */
function createStarContainer() {
  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '10000',
    overflow: 'hidden',
  });
  document.body.appendChild(container);
  return container;
}

/**
 * Emit star particles from a cell, flying toward the achievements icon.
 * Stars launch one by one, 200ms apart, each with unique path and speed.
 *
 * @param {HTMLElement} cell - Source cell element
 * @param {HTMLElement} container - Star particle container
 * @param {{x: number, y: number}} target - Achievements icon center position
 */
function emitStarsFromCell(cell, container, target) {
  const cellRect = cell.getBoundingClientRect();
  const originX = cellRect.left + cellRect.width / 2;
  const originY = cellRect.top + cellRect.height / 2;

  for (let i = 0; i < STARS_PER_LETTER; i++) {
    setTimeout(() => {
      const starUrl = STAR_URLS[Math.floor(Math.random() * STAR_URLS.length)];
      const startSize = STAR_MIN_SIZE + Math.random() * (STAR_MAX_SIZE - STAR_MIN_SIZE);

      // Spread each star horizontally so they don't stack
      const spreadX = (i - (STARS_PER_LETTER - 1) / 2) * 35 + (Math.random() - 0.5) * 20;

      // Vary travel duration per star so they don't move in lockstep
      const travelDuration = STAR_TRAVEL_DURATION_MS + (Math.random() - 0.5) * 600;

      animateStarParticle(
        container,
        starUrl,
        originX + spreadX,
        originY,
        target.x,
        target.y,
        startSize,
        travelDuration
      );
    }, i * 500);
  }
}

/**
 * Animate a single star particle along the defined path:
 * - Phase 1 (0-50%): Rise straight up, grow from STAR_START_SIZE to STAR_MID_SIZE, opacity 1
 * - Phase 2 (50-100%): Curve toward achievements icon, shrink to STAR_END_SIZE, opacity 1→0
 *
 * @param {HTMLElement} container
 * @param {string} url - Star SVG URL
 * @param {number} ox - Origin X
 * @param {number} oy - Origin Y
 * @param {number} destX - Achievements icon X
 * @param {number} destY - Achievements icon Y
 */
function animateStarParticle(container, url, ox, oy, destX, destY, startSize, travelDuration) {
  const el = document.createElement('img');
  el.src = url;
  el.alt = '';
  el.setAttribute('aria-hidden', 'true');
  Object.assign(el.style, {
    position: 'absolute',
    width: startSize + 'px',
    height: startSize + 'px',
    objectFit: 'contain',
    pointerEvents: 'none',
    willChange: 'transform, opacity, width, height',
    left: '0',
    top: '0',
    opacity: '1',
  });
  container.appendChild(el);

  // Midpoint: halfway up from origin to top of viewport
  const midY = oy - (oy / 2);
  const midX = ox;

  // Unique sine-wave parameters per star for organic flow
  const amplitude = 15 + Math.random() * 35; // 15-50px lateral wobble
  const frequency = 1.5 + Math.random() * 3; // 1.5-4.5 cycles
  const phaseOffset = Math.random() * Math.PI * 2; // random start angle

  const startTime = performance.now();
  const rotation = Math.random() * 360;

  function tick(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / travelDuration, 1);

    let x, y, size, opacity;

    if (t <= 0.5) {
      // Phase 1: rise up with sine-wave wobble
      const phase = t / 0.5;
      const eased = 1 - Math.pow(1 - phase, 2);

      const baseX = ox;
      const baseY = oy + (midY - oy) * eased;

      // Sine wave lateral wobble (fades slightly as it rises)
      const wobble = Math.sin(frequency * Math.PI * 2 * phase + phaseOffset) * amplitude * (1 - phase * 0.3);

      x = baseX + wobble;
      y = baseY;
      size = startSize + (STAR_MID_SIZE - startSize) * eased;
      opacity = 1;
    } else {
      // Phase 2: curve toward achievements icon
      const phase = (t - 0.5) / 0.5;
      const eased = phase * phase;

      x = midX + (destX - midX) * eased;
      y = midY + (destY - midY) * eased;
      size = STAR_MID_SIZE + (STAR_END_SIZE - STAR_MID_SIZE) * eased;
      opacity = 1 - phase;
    }

    const currentRotation = rotation + t * 360;
    el.style.transform = `translate(${x - size / 2}px, ${y - size / 2}px) rotate(${currentRotation}deg)`;
    el.style.width = size + 'px';
    el.style.height = size + 'px';
    el.style.opacity = opacity;

    if (t < 1) {
      requestAnimationFrame(tick);
    } else {
      el.remove();
    }
  }

  requestAnimationFrame(tick);
}

// ═══════════════════════════════════════════════════════════════════
// INTERNAL — Helpers
// ═══════════════════════════════════════════════════════════════════

/**
 * Get the current screen position of the achievements nav icon.
 * Recalculates each time to handle dynamic layouts.
 *
 * @returns {{x: number, y: number}} Center coordinates of the achievements icon
 */
function getAchievementsPosition() {
  const achievementsBtn = document.querySelector('[data-route="achievements"]');
  if (achievementsBtn) {
    const rect = achievementsBtn.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }
  // Fallback: top-right area
  return { x: window.innerWidth - 60, y: 40 };
}

/**
 * Play the reward sound effect based on difficulty level.
 * @param {string} [difficulty] - 'easy', 'medium', or 'hard'
 */
function playRewardSound(difficulty) {
  try {
    let soundPath = REWARD_SOUND_PATH;
    if (difficulty === 'easy') soundPath = REWARD_SOUND_PATH_EASY;
    else if (difficulty === 'medium') soundPath = REWARD_SOUND_PATH_MEDIUM;

    const audio = new Audio(soundPath);
    audio.volume = REWARD_SOUND_VOLUME;
    audio.play().catch(() => { /* ignore autoplay restrictions */ });
  } catch {
    /* ignore audio errors */
  }
}

/**
 * Simple delay helper.
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Play the last-word reward animation.
 * Instead of stars from letters, one star per word rises from each word
 * in the word bank at the bottom of the page, flying to the achievements icon.
 * Stars emit one by one, 300ms apart.
 *
 * @param {HTMLElement} container - The word search container element
 * @returns {Promise<void>} Resolves when all stars have finished
 */
export async function playLastWordReward(container) {
  if (!SHOW_WORD_SEARCH_REWARD_ANIMATION) return;

  playRewardSound();

  const achievementsTarget = getAchievementsPosition();
  const starContainer = createStarContainer();

  // Get all word elements in the word bank (each found word = one star)
  const wordElements = container.querySelectorAll('.ws-word');
  if (!wordElements.length) {
    starContainer.remove();
    return;
  }

  const totalStars = wordElements.length;
  let completed = 0;

  return new Promise((resolve) => {
    wordElements.forEach((wordEl, i) => {
      setTimeout(() => {
        const rect = wordEl.getBoundingClientRect();
        const originX = rect.left + rect.width / 2;
        const originY = rect.top + rect.height / 2;

        const starUrl = STAR_URLS[Math.floor(Math.random() * STAR_URLS.length)];
        const startSize = STAR_MIN_SIZE + Math.random() * (STAR_MAX_SIZE - STAR_MIN_SIZE);
        const travelDuration = STAR_TRAVEL_DURATION_MS + (Math.random() - 0.5) * 400;

        animateStarParticle(
          starContainer,
          starUrl,
          originX,
          originY,
          achievementsTarget.x,
          achievementsTarget.y,
          startSize,
          travelDuration
        );

        completed++;
        if (completed >= totalStars) {
          // Wait for last star to finish traveling
          setTimeout(() => {
            starContainer.remove();
            resolve();
          }, travelDuration);
        }
      }, i * 300);
    });
  });
}
